import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';

import { ModalController } from '@ionic/angular';
import { Observable, catchError, map, of } from 'rxjs';
import { AnalyticsService } from 'src/app/core/services/analytics/analytics.service';
import { AnalyticsServicesNameEnum } from 'src/app/core/services/analytics/enums/analytics-services-name.enum';
import { FlexJobsService } from 'src/app/pages/flex/services/flex-jobs.service';
import { EventTrackingEnum } from 'src/app/shared/enums/event-tracking.enum';
import { BudgetSupportTypesFromAgreementRatesResult } from 'src/app/shared/graphql/agreements/get-agreement-rates-from-agreement-query.graphql';
import { GetAgreementRatesFromAgreementQueryService } from 'src/app/shared/graphql/agreements/get-agreement-rates-from-agreement-query.graphql.autogenerated';
import { JobDetail } from 'src/app/shared/models/gql/job-detail.model';
import { BudgetSupportType } from 'src/app/shared/models/gql/rate.model';
import { AccountService } from 'src/app/shared/services/account.service';
import { CancelBookingComponent } from '../cancel-booking/cancel-booking.component';
import { CancelJobComponent } from '../cancel-job/cancel-job.component';

@Component({
  selector: 'app-flex-job-summary',
  templateUrl: './job-summary.component.html',
  styleUrls: ['./job-summary.component.scss'],
})
export class FlexJobSummaryComponent implements OnInit {
  @Input() job: JobDetail;

  /** Flag to check whether to show the rates info banner */
  @Input() shouldShowRatesInfoBanner = true;

  @Output() retrieveJob = new EventEmitter();

  // default service category
  defaultServiceCategory$: Observable<BudgetSupportType[] | BudgetSupportTypesFromAgreementRatesResult>;

  // Checks whether the curent user type is a carer
  isCarer = true;

  constructor(
    public accountService: AccountService,
    public modalController: ModalController,
    private analyticsService: AnalyticsService,
    private agreementRatesQueryService: GetAgreementRatesFromAgreementQueryService,
    private flexJobsService: FlexJobsService,
  ) {}

  ngOnInit(): void {
    this.isCarer = this.accountService.userIs('carer');
    if (this.isCarer) {
      this.analyticsService.track(
        EventTrackingEnum.JOB_VIEWED,
        { job_id: this.job?.id?.toString() },
        AnalyticsServicesNameEnum.SEGMENT_SERVICE,
      );
    }

    this.setDefaultServiceCategory();
  }

  async cancelBooking() {
    const modal = await this.modalController.create(this.cancellationModalOptions());

    // https://github.com/ionic-team/ionic-framework/issues/26872
    modal.shadowRoot.querySelector('.modal-wrapper').removeAttribute('aria-modal');

    modal.onDidDismiss().then((res) => {
      if (res?.data) {
        this.retrieveJob.emit();
      }
    });
    modal.present();
  }

  cancellationModalOptions() {
    if (this.job?.status?.clientStatus === 'open') {
      return {
        component: CancelJobComponent,
        componentProps: {
          jobId: this.job.id,
        },
        showBackdrop: true,
        cssClass: 'fullpage-modal',
      };
    }
    return {
      component: CancelBookingComponent,
      componentProps: {
        userId: this.job?.clientFields?.acceptedMatch?.user?.id,
        channelUrl: this.job?.workerFields?.conversation?.sendbirdChannelDetail?.channelUrl,
      },
      showBackdrop: true,
      cssClass: 'fullpage-modal',
    };
  }

  showCancel() {
    // only client can cancel and that too only 'open' and 'filled' jobs from job summary component.
    return (
      this.accountService.userIs('consumer') &&
      (this.job?.status?.clientStatus === 'open' || this.job?.status?.clientStatus === 'fulfilled')
    );
  }

  /**
   * Creates a stream responsible for fetching from the server the default service category.
   */
  private setDefaultServiceCategory(): void {
    this.defaultServiceCategory$ =
      this.job?.status?.clientStatus === 'fulfilled'
        ? this.getDefaultServiceCategoryFromAgreement()
        : this.flexJobsService.getDefaultServiceCategoryFromJob(
            this.job?.client?.id?.toString(),
            this.job?.id?.toString(),
          );
  }

  /**
   * Gets the default service category from an agreement (if existing).
   *
   * @returns default service category present in the agreement rate.
   */
  private getDefaultServiceCategoryFromAgreement(): Observable<BudgetSupportTypesFromAgreementRatesResult> {
    const agreementId = this.getAgreementId();
    if (!agreementId) return of([]);

    return this.agreementRatesQueryService.fetch({ id: agreementId as string }).pipe(
      map((result) => {
        const agreementRates = result.data.agreement.agreementRates;
        if (!agreementRates[0]?.budgetSupportTypes?.length) return [];
        return agreementRates[0].budgetSupportTypes;
      }),
      catchError((error) => {
        console.error('[FlexJobSummaryComponent] Error fetching the agreement related to this job', error);
        return of(null);
      }),
    );
  }

  /**
   * Gets the agreement id from the job object.
   *
   * @returns agreement id
   */
  private getAgreementId(): string | number {
    return this.accountService.userIs('carer')
      ? this.job?.workerFields?.agreement?.id
      : this.job?.clientFields?.acceptedMatch?.agreement?.id;
  }
}
